// Reasons a distributor can pick when raising a query (Query Tracker). No
// delete — deactivating hides a reason from new queries but old queries keep
// their snapshot label.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";

const CODE_RE = /^[a-z0-9_]{2,64}$/;
const EMPTY_ROW = { code: "", label: "", requires_date_range: false, order_index: 100, is_active: true };

function toCode(v) {
  return v.toLowerCase().replace(/[\s-]+/g, "_").replace(/[^a-z0-9_]/g, "");
}

function validOrder(n) {
  return Number.isInteger(n) && n >= 0 && n <= 999;
}

export default function QueryReasons() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newRow, setNewRow] = useState(EMPTY_ROW);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/distributor-queries/reasons", { params: { include_inactive: true } });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveItem = async (row, updates) => {
    try {
      await api.patch(`/distributor-queries/reasons/${row.id}`, updates);
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const create = async () => {
    const label = newRow.label.trim();
    if (!CODE_RE.test(newRow.code)) { toast.error("Code must be 2-64 characters: a-z, 0-9 or _."); return; }
    if (!label) { toast.error("Enter a label."); return; }
    if (!validOrder(newRow.order_index)) { toast.error("Order must be a whole number from 0 to 999."); return; }
    setAdding(true);
    try {
      await api.post("/distributor-queries/reasons", { ...newRow, label });
      toast.success("Added");
      setNewRow(EMPTY_ROW);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-query-reasons">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Query Reasons" }]}
        title="Query Reasons"
        subtitle="Reasons distributors pick when raising a query. Turn on 'Needs date range' when the team needs a From/To period (e.g. ledger). Deactivating hides a reason from new queries only."
        testid="query-reasons-page-header"
      />

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="font-bold text-neutral-800">Reasons</div>
            <div className="text-xs text-neutral-500">{items.length} reason(s)</div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-neutral-200">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-left">
                <tr>
                  <th className="px-3 py-2 font-medium text-neutral-600">Code</th>
                  <th className="px-3 py-2 font-medium text-neutral-600">Label</th>
                  <th className="px-3 py-2 font-medium text-neutral-600 w-28">Needs date range</th>
                  <th className="px-3 py-2 font-medium text-neutral-600 w-24">Order</th>
                  <th className="px-3 py-2 font-medium text-neutral-600 w-20">Active</th>
                  <th className="px-3 py-2 w-20" />
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {busy && items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-neutral-500 py-6">Loading…</td>
                  </tr>
                )}
                {items.map((it) => (
                  <EditableRow key={it.id} row={it} onSave={saveItem} />
                ))}
                <tr className="bg-neutral-50/60" data-testid="query-reason-new-row">
                  <td className="px-3 py-2">
                    <Input
                      data-testid="query-reason-new-code"
                      value={newRow.code}
                      onChange={(e) => setNewRow({ ...newRow, code: toCode(e.target.value) })}
                      placeholder="code"
                      maxLength={64}
                      className="h-9 min-w-[120px] font-mono text-xs"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Input
                      data-testid="query-reason-new-label"
                      value={newRow.label}
                      onChange={(e) => setNewRow({ ...newRow, label: e.target.value })}
                      placeholder="Label"
                      maxLength={120}
                      className="h-9 min-w-[160px]"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Switch
                      checked={newRow.requires_date_range}
                      onCheckedChange={(v) => setNewRow({ ...newRow, requires_date_range: v })}
                      data-testid="query-reason-new-dates"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Input
                      type="number" min={0} max={999}
                      value={newRow.order_index}
                      onChange={(e) => setNewRow({ ...newRow, order_index: Number(e.target.value) })}
                      className="h-9 w-20"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Switch
                      checked={newRow.is_active}
                      onCheckedChange={(v) => setNewRow({ ...newRow, is_active: v })}
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      size="sm"
                      onClick={create}
                      disabled={adding || !newRow.code || !newRow.label.trim()}
                      className="tulsi-gradient text-white h-8"
                      data-testid="query-reason-new-add"
                    >
                      <Plus className="h-3 w-3 mr-1" /> Add
                    </Button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function EditableRow({ row, onSave }) {
  const [label, setLabel] = useState(row.label);
  const [requires, setRequires] = useState(!!row.requires_date_range);
  const [order, setOrder] = useState(row.order_index);
  const [active, setActive] = useState(row.is_active !== false);

  useEffect(() => {
    setLabel(row.label);
    setRequires(!!row.requires_date_range);
    setOrder(row.order_index);
    setActive(row.is_active !== false);
  }, [row]);

  const save = () => {
    const updates = {};
    if (label.trim() !== row.label) updates.label = label.trim();
    if (requires !== !!row.requires_date_range) updates.requires_date_range = requires;
    if (order !== row.order_index) updates.order_index = order;
    if (active !== (row.is_active !== false)) updates.is_active = active;
    if (!Object.keys(updates).length) return;
    if ("label" in updates && !updates.label) { toast.error("Label cannot be empty."); return; }
    if ("order_index" in updates && !validOrder(updates.order_index)) {
      toast.error("Order must be a whole number from 0 to 999."); return;
    }
    onSave(row, updates);
  };

  const dirty = label.trim() !== row.label || requires !== !!row.requires_date_range
    || order !== row.order_index || active !== (row.is_active !== false);

  return (
    <tr data-testid={`query-reason-row-${row.id}`} className={active ? "" : "opacity-60"}>
      <td className="px-3 py-2 font-mono text-xs text-neutral-600">{row.code}</td>
      <td className="px-3 py-2">
        <Input value={label} maxLength={120} onChange={(e) => setLabel(e.target.value)} className="h-9 min-w-[160px]" />
      </td>
      <td className="px-3 py-2">
        <Switch checked={requires} onCheckedChange={setRequires} data-testid={`query-reason-dates-${row.id}`} />
      </td>
      <td className="px-3 py-2">
        <Input type="number" min={0} max={999} value={order} onChange={(e) => setOrder(Number(e.target.value))} className="h-9 w-20" />
      </td>
      <td className="px-3 py-2">
        <Switch checked={active} onCheckedChange={setActive} data-testid={`query-reason-active-${row.id}`} />
      </td>
      <td className="px-3 py-2 text-right">
        <Button
          size="sm"
          variant="outline"
          disabled={!dirty}
          onClick={save}
          className="h-8"
          data-testid={`query-reason-save-${row.id}`}
        >
          <Save className="h-3 w-3" />
        </Button>
      </td>
    </tr>
  );
}
